import { useEffect, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import { NotasManager } from "@/components/NotasManager";

export const DESTINOS = [
  { key: "fab_cos", label: "Fabricación y Costuras" },
  { key: "costuras", label: "Solo Costuras" },
  { key: "entregas", label: "Entregas" },
  { key: "bases", label: "Solo Bases" },
  { key: "todos", label: "Todos los módulos" },
];
const DEFAULT_DEST = { notas_hoja: "fab_cos", notas_fab: "fab_cos", notas_cos: "costuras", notas_ent: "entregas", notas_bas: "bases" };

/**
 * Notas del pedido. modulo = campo visible (notas_hoja=todas, notas_fab, notas_cos, notas_ent).
 * Agrega nota nueva con destino; "Editar" abre el listado para editar/eliminar.
 */
export default function EditableNote({ orderId, initial = "", onSaved, modulo = "notas_hoja", destino }) {
  const [value, setValue] = useState("");
  const [dest, setDest] = useState(destino || DEFAULT_DEST[modulo]);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [manage, setManage] = useState(false);
  const ref = useRef(null);
  const enHoja = modulo === "notas_hoja";

  useEffect(() => { if (editing) ref.current?.focus(); }, [editing]);

  const done = (data) => onSaved?.(data?.[modulo] ?? data?.notas_hoja ?? "", data);

  const save = async () => {
    if (!value.trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      const { data } = await api.patch(`/orders/${orderId}/notas-hoja`, { notas_hoja: value.trim(), destino: dest });
      toast.success("Nota agregada");
      setValue("");
      done(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  return (
    <div className="space-y-1">
      {initial && (
        <div className="text-[11px] bg-yellow-50 border border-yellow-200 rounded p-2 text-zinc-800 whitespace-pre-wrap" data-testid={`note-view-${orderId}`}>
          {initial}
        </div>
      )}
      {editing ? (
        <div className="space-y-1">
          {enHoja && (
            <select value={dest} onChange={(e) => setDest(e.target.value)} disabled={saving}
              className="w-full text-[11px] rounded border border-zinc-300 bg-white p-1" data-testid={`note-destino-${orderId}`}>
              {DESTINOS.filter((d) => d.key !== "todos").map((d) => <option key={d.key} value={d.key}>{d.label}</option>)}
            </select>
          )}
          <textarea
            ref={ref}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") { setValue(""); setEditing(false); }
              if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) save();
            }}
            rows={2}
            disabled={saving}
            placeholder="Nueva nota..."
            className="w-full min-w-[160px] text-[11px] rounded border border-yellow-300 bg-yellow-50 p-2 focus:outline-none focus:ring-2 focus:ring-yellow-400"
            data-testid={`note-textarea-${orderId}`}
          />
          <div className="flex gap-2">
            <button onClick={save} disabled={saving} className="text-[11px] font-semibold text-white bg-[#002FA7] rounded px-2 py-0.5" data-testid={`note-save-${orderId}`}>Guardar</button>
            <button onClick={() => { setValue(""); setEditing(false); }} className="text-[11px] text-zinc-500" data-testid={`note-cancel-${orderId}`}>Cancelar</button>
          </div>
        </div>
      ) : (
        <div className="flex gap-3">
          <button onClick={() => setEditing(true)} className="text-[11px] text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1" data-testid={`note-add-${orderId}`}>
            <Plus className="w-3 h-3" /> Agregar nota
          </button>
          {initial && (
            <button onClick={() => setManage(true)} className="text-[11px] text-zinc-400 hover:text-[#002FA7] inline-flex items-center gap-1" data-testid={`note-manage-${orderId}`}>
              <Pencil className="w-3 h-3" /> Editar
            </button>
          )}
        </div>
      )}
      {manage && <NotasManager orderId={orderId} modulo={modulo} onClose={() => setManage(false)} onSaved={done} />}
    </div>
  );
}
